import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ResendIcon } from 'assets/icons/reset.svg';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';

import { getTextDate } from '../../../../../utils/formattedData';

import type ISelectSupplierTable from './interfaces/ItemsTableMainEmails';

import styles from './scss/ItemsTableMainEmails.module.scss';

function ItemsTableMainEmails({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  handleResend,
  disableActions,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');
  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Analysis table body"
        role="region"
      >
        <Table className={styles.table}>
          <TableHead className={styles.header}>
            {headCells.map(({
              id, label,
            }) => {
              if (id === 'resend' && disableActions) {
                return null;
              }
              return (
                <TableCell head className={styles.tableCell} key={id}>
                  <div>
                    <span className={styles.tableCellLabel}>
                      {t(label)}
                    </span>
                  </div>
                </TableCell>
              );
            })}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  {item?.respondent || hyphen}
                </TableCell>
                <TableCell>
                  {item?.email || hyphen}
                </TableCell>
                <TableCell>
                  {item?.IRO?.type ? t(`materialityAssessment.${item.IRO.type}`) : hyphen}
                </TableCell>
                <TableCell>
                  {`${item?.IRO?.standard} ${item?.IRO?.topic}` || hyphen}
                </TableCell>
                <TableCell>
                  {item?.valueChainMap?.name || hyphen}
                </TableCell>
                <TableCell>
                  {getTextDate(item?.createdAt) || hyphen}
                </TableCell>
                <TableCell>
                  <span className={`${styles.status} ${item?.status ? styles[item?.status] : ''}`}>
                    {item?.status ? t(`materialityAssessment.${item.status}`) : hyphen}
                  </span>
                </TableCell>
                {!disableActions && (
                  <TableCell>
                    <div className={styles.actionCell}>
                      <IconButton
                        icon={<ResendIcon />}
                        onClick={() => handleResend(item.id)}
                        disabled={disableActions}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTableMainEmails;
